import type { JourneyId } from './dynatrace-journey';

// Keyed per journeyId so concurrent/unrelated journeys don't clobber each other's start time.
// Uses localStorage (not sessionStorage) since it must survive the OS killing the WebView
// while the app is backgrounded mid-journey (e.g. joining charger Wi-Fi in native Settings).
function getJourneyTimerStorageKey(journeyId: JourneyId) {
  return `sc_dynatrace_${journeyId}_journey_start_ms`;
}

export function startJourneyTimer(journeyId: JourneyId) {
  try {
    window.localStorage.setItem(getJourneyTimerStorageKey(journeyId), String(Date.now()));
  } catch {
    // localStorage may be unavailable (e.g. private browsing); timing is best-effort.
  }
}

export function getJourneyElapsedMs(journeyId: JourneyId): number | undefined {
  try {
    const startedAt = window.localStorage.getItem(getJourneyTimerStorageKey(journeyId));
    return startedAt ? Date.now() - Number(startedAt) : undefined;
  } catch {
    return undefined;
  }
}

export function clearJourneyTimer(journeyId: JourneyId) {
  try {
    window.localStorage.removeItem(getJourneyTimerStorageKey(journeyId));
  } catch {
    // no-op
  }
}

// Starts the timer only if it isn't already running, so an entry point that isn't the journey's
// primary "start" marker (e.g. a second onboarding entry point that skips it) can't clobber an
// already-accurate elapsed time, while still guaranteeing one is running by the time it matters.
export function ensureJourneyTimerStarted(journeyId: JourneyId) {
  if (getJourneyElapsedMs(journeyId) === undefined) {
    startJourneyTimer(journeyId);
  }
}
